/**
 * ============================================================================
 * BAG ADD-ONS — gift wrap and donation, as ordinary cart lines.
 * ============================================================================
 *
 * These buttons TAKE MONEY, so everything here is deliberately boring: one
 * request per press, the server's own section HTML swapped back in, and the
 * button disabled while it is in flight.
 *
 * ⚠ IT ASKS THE SERVER FOR THE SECTIONS RATHER THAN RELOADING OR PATCHING.
 *
 * `/cart/add.js` and `/cart/change.js` both accept a `sections` list and
 * return the re-rendered HTML for each. That is the same mechanism cart.js
 * uses, so the totals, the applied-discount list, the dock's mirrors and the
 * header bubble all update from ONE source of truth — the server — instead of
 * this file trying to keep a second copy of the arithmetic. A reload would
 * also be correct and would throw away the visitor's scroll position on the
 * one page where they are mid-decision.
 *
 * ⚠ THE DONATION AMOUNTS BEHAVE LIKE RADIOS. A visitor who taps 20 after 10
 * means twenty, not thirty. Every other variant in the group is set to zero in
 * the same pass, before the chosen one is added.
 */

(() => {
  /**
   * ⚠ THESE ARE SECTION IDS, NOT SECTION TYPES, AND THE DIFFERENCE IS SILENT.
   *
   * The first version sent the literal strings 'main-cart-items' and
   * 'main-cart-footer'. Shopify keys the Section Rendering response by the
   * requested SECTION ID — `template--27475439452233__cart-items` — so nothing
   * matched, `applySections` found no HTML to swap, and every request
   * SUCCEEDED while the page never changed.
   *
   * Measured: adding a Rs. 35 wrap and a Rs. 50 donation left the dock total
   * at Rs. 4,318.20 and the breakdown rows absent, while /cart.js showed all
   * four lines. A green request and a stale page is the worst shape of bug —
   * there is nothing in the console to find.
   *
   * cart.js reads them off the rendered elements' `data-id`; so does this.
   */
  const sectionIds = () => {
    const ids = [];
    const items = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');
    if (items && items.dataset.id) ids.push(items.dataset.id);
    if (footer && footer.dataset.id) ids.push(footer.dataset.id);
    ids.push('cart-icon-bubble');
    return ids;
  };

  const busy = (button, on) => {
    if (!button) return;
    button.disabled = on;
    button.setAttribute('aria-busy', on ? 'true' : 'false');
  };

  /**
   * Swap in what the server rendered.
   *
   * Mirrors cart.js's own targets: `.js-contents` for the two cart sections
   * (never the whole section — the tab bar and the dock live outside it and
   * must not be replaced), and the whole element for the header bubble.
   */
  const applySections = (sections) => {
    if (!sections) return;

    const swapInner = (hostSelector, sectionKey, innerSelector) => {
      const host = document.querySelector(hostSelector);
      const html = sections[sectionKey];
      if (!host || !html) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const source = innerSelector ? parsed.querySelector(innerSelector) : parsed.body.firstElementChild;
      const target = innerSelector ? host.querySelector(innerSelector) : host;
      if (!source || !target) return;
      target.innerHTML = source.innerHTML;
    };

    const itemsId = document.getElementById('main-cart-items')?.dataset.id;
    const footerId = document.getElementById('main-cart-footer')?.dataset.id;

    swapInner('#main-cart-items', itemsId, '.js-contents');
    swapInner('#main-cart-footer', footerId, '.js-contents');
    swapInner('#main-cart-footer', footerId, '.js-cart-goal');
    swapInner('#cart-icon-bubble', 'cart-icon-bubble', null);

    // Tell the rest of the theme, so the goal meter animates and the dock's
    // mirrors re-run. Dawn's own modules listen for this.
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    if (window.PUB_SUB_EVENTS && typeof window.publish === 'function') {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-addons' });
    }
  };

  const post = async (url, payload) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        ...payload,
        sections: sectionIds().join(','),
        sections_url: window.location.pathname,
      }),
    });
    const text = await res.text();
    let data = null;
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
    return { ok: res.ok, status: res.status, data };
  };

  /**
   * ⚠ EVERY ADD-ON LINE IS STAMPED WITH `_addon`.
   *
   * Shopify has no way to put money on a cart total without a line item —
   * there is no fee primitive in Liquid or the AJAX cart. So gift wrap and a
   * donation ARE lines, and the merchant's objection ("added to the total
   * cost and not as an additional product") is about PRESENTATION.
   *
   * This property is what lets Liquid tell an add-on from a product: the item
   * list skips them, the price breakdown gives each one its own row, and the
   * item count leaves them out. Matching on product handles would have meant
   * teaching two separate sections the same two settings.
   *
   * The leading underscore is Shopify's convention for a property hidden from
   * the customer in checkout and on the order — this is bookkeeping, not
   * something the buyer needs to read.
   */
  const addVariant = (id, kind) =>
    post('/cart/add.js', {
      items: [{ id: Number(id), quantity: 1, properties: { _addon: kind || 'addon' } }],
    });

  /* `id` here is a VARIANT id, which /cart/change.js accepts in place of a
     line number. Quantity 0 removes the line. Using the variant id means this
     never has to know which position the line is in — and positions move
     whenever anything else in the bag changes. */
  const setQuantity = (id, quantity) => post('/cart/change.js', { id: String(id), quantity });

  const failed = (button, result) => {
    const message = (result.data && (result.data.description || result.data.message)) || 'That could not be added.';
    // Announced rather than alerted: this sits in a rail the visitor may not
    // be looking at, and an alert would steal the whole page for a line item.
    const host = button.closest('.bag-addon');
    if (!host) return;
    let note = host.querySelector('[data-addon-error]');
    if (!note) {
      note = document.createElement('p');
      note.className = 'bag-addon__error';
      note.setAttribute('data-addon-error', '');
      note.setAttribute('role', 'status');
      host.appendChild(note);
    }
    note.textContent = message;
  };

  /* ------------------------------------------------------------------------
   * THE GIFT PACKAGING DIALOG
   *
   * Gift wrap is the one add-on where pressing Add immediately would be wrong:
   * the card IS the product, and a wrap added without a message puts a blank
   * card in a real package. So the row opens a dialog, and the dialog adds.
   *
   * Focus is borrowed and returned, Escape closes, Tab is trapped, and the
   * scrim cannot eat clicks before the open class lands — the same contract as
   * the gift popup, for the same reasons.
   * --------------------------------------------------------------------- */

  const EXIT_MS = 140;
  let wrapLastFocused = null;
  let wrapExitTimer = null;

  const wrapModal = () => document.querySelector('[data-giftwrap-modal]');

  const wrapFocusable = (root) =>
    [...root.querySelectorAll('button, input, textarea, select, [href], [tabindex]:not([tabindex="-1"])')].filter(
      (el) => !el.disabled && el.offsetParent !== null,
    );

  const wrapCount = () => {
    const area = document.querySelector('[data-giftwrap-counted]');
    const out = document.querySelector('[data-giftwrap-remaining]');
    if (!area || !out) return;
    // Counts DOWN, matching the reference: the number that matters is how much
    // room is left, not how much has been used.
    const left = Math.max(0, (area.maxLength || 200) - area.value.length);
    if (out.textContent !== String(left)) out.textContent = left;
  };

  const closeWrap = () => {
    const modal = wrapModal();
    if (!modal || modal.hidden) return;
    modal.classList.remove('is-open');
    document.documentElement.classList.remove('giftwrap-pop-open');
    if (wrapLastFocused && document.contains(wrapLastFocused)) wrapLastFocused.focus();
    wrapLastFocused = null;
    window.clearTimeout(wrapExitTimer);
    wrapExitTimer = window.setTimeout(() => {
      if (!modal.classList.contains('is-open')) modal.hidden = true;
    }, EXIT_MS);
  };

  const openWrap = () => {
    const modal = wrapModal();
    if (!modal) return;
    window.clearTimeout(wrapExitTimer);
    wrapLastFocused = document.activeElement;
    modal.hidden = false;
    window.requestAnimationFrame(() => modal.classList.add('is-open'));
    document.documentElement.classList.add('giftwrap-pop-open');
    wrapCount();
    // The recipient field, not the close button: this dialog exists to be
    // filled in, and the first thing a keyboard user wants is the first field.
    const first = modal.querySelector('#GiftwrapTo');
    if (first) first.focus();
  };

  document.addEventListener('input', (event) => {
    if (event.target.matches('[data-giftwrap-counted]')) wrapCount();
  });

  document.addEventListener('keydown', (event) => {
    const modal = wrapModal();
    if (!modal || modal.hidden) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeWrap();
      return;
    }
    if (event.key !== 'Tab') return;
    const panel = modal.querySelector('[data-giftwrap-panel]');
    const items = wrapFocusable(panel);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.addEventListener('click', async (event) => {
    if (event.target.closest('[data-giftwrap-open]')) {
      event.preventDefault();
      openWrap();
      return;
    }
    if (event.target.closest('[data-giftwrap-dismiss]')) {
      event.preventDefault();
      closeWrap();
      return;
    }

    const apply = event.target.closest('[data-giftwrap-apply]');
    if (!apply) return;
    event.preventDefault();
    if (apply.disabled) return;
    busy(apply, true);

    try {
      const id = apply.dataset.variantId;
      const val = (sel) => (document.querySelector(sel) || {}).value || '';
      /* Only non-empty fields are sent. An order line carrying `To: ""` reads
         as a card that was meant to say something and lost it. */
      const props = { _addon: 'giftwrap' };
      const to = val('#GiftwrapTo').trim();
      const msg = val('#GiftwrapMessage').trim();
      const from = val('#GiftwrapFrom').trim();
      if (to) props.To = to;
      if (msg) props.Message = msg;
      if (from) props.From = from;

      /* Replace, never stack. Changing the message must not leave the old wrap
         line behind at full price beside the new one — a second Rs. 35 charge
         for editing a sentence. */
      await setQuantity(id, 0);
      const result = await post('/cart/add.js', {
        items: [{ id: Number(id), quantity: 1, properties: props }],
      });

      if (!result.ok) {
        failed(apply, result);
        return;
      }
      closeWrap();
      applySections(result.data && result.data.sections);
    } catch (error) {
      failed(apply, { data: { message: 'Network error. Please try again.' } });
    } finally {
      busy(apply, false);
    }
  });

  document.addEventListener('click', async (event) => {
    const add = event.target.closest('[data-addon-add]');
    const remove = event.target.closest('[data-addon-remove]');
    const donate = event.target.closest('[data-addon-donation]');
    const button = add || remove || donate;
    if (!button) return;

    event.preventDefault();
    if (button.disabled) return;
    busy(button, true);

    try {
      const id = button.dataset.variantId;
      let result;

      if (remove) {
        result = await setQuantity(id, 0);
      } else if (donate) {
        // Clear the rest of the group first, so amounts replace rather than
        // accumulate. Sequential on purpose: these are writes to one cart, and
        // firing them together is how you get a race over the same resource.
        const group = (button.dataset.group || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        const alreadyOn = button.getAttribute('aria-pressed') === 'true';
        for (const other of group) {
          if (other === id) continue;
          await setQuantity(other, 0);
        }
        // Pressing the selected amount again clears it — the only way to undo
        // a donation without a separate control.
        result = alreadyOn ? await setQuantity(id, 0) : await addVariant(id, 'donation');
      } else {
        result = await addVariant(id, button.dataset.addonKind || 'giftwrap');
      }

      if (!result.ok) {
        failed(button, result);
        return;
      }
      applySections(result.data && result.data.sections);
    } catch (error) {
      failed(button, { data: { message: 'Network error. Please try again.' } });
    } finally {
      busy(button, false);
    }
  });
})();
